import React, { useState } from 'react';
import {
  CalendarCheck,
  House,
  Drop,
  Sparkle,
  PawPrint,
  Phone,
  ArrowRight,
  Check,
  Cake,
  Camera,
  Gift,
  Confetti,
  Star,
  Heart,
  ShieldCheck,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import OurFacility from '../components/landing/OurFacility';
import BrandHero, { BrandHeroCopy } from '../components/landing/BrandHero';
import BookingEnquiryDialog from '../components/booking/BookingEnquiryDialog';

const services = [
  {
    icon: House,
    title: 'Overnight Boarding',
    desc: 'Individual suites with cosy bedding, climate control, and round-the-clock supervision.',
    color: 'bg-amber-100 text-amber-700',
  },
  {
    icon: PawPrint,
    title: 'Daycare',
    desc: 'Structured play, socialisation sessions, and rest cycles throughout the day.',
    color: 'bg-orange-100 text-orange-700',
  },
  {
    icon: Drop,
    title: 'Swimming Pool',
    desc: 'Hydrotherapy and recreational swim sessions in a clean, temperature-controlled pool.',
    color: 'bg-blue-100 text-blue-700',
  },
  {
    icon: Sparkle,
    title: 'Grooming Spa',
    desc: 'Full-service grooming — bath, haircut, nail trim, ear cleaning, and de-shedding.',
    color: 'bg-rose-100 text-rose-700',
  },
  {
    icon: Heart,
    title: 'Recreation & Play',
    desc: 'Off-leash play zones, agility equipment, and supervised group play sessions.',
    color: 'bg-emerald-100 text-emerald-700',
  },
  {
    icon: ShieldCheck,
    title: 'Health Monitoring',
    desc: 'Daily health checks, meal logging, and incident reporting with photo updates.',
    color: 'bg-violet-100 text-violet-700',
  },
];

const birthdayFeatures = [
  { icon: Cake, title: 'Custom Pet Cake', sub: 'Pup-safe flavours & decor' },
  { icon: Camera, title: 'Photo & Video Shoot', sub: 'Curated portrait sessions' },
  { icon: Gift, title: 'Goodie Bags', sub: 'Treats for furry guests' },
  { icon: Sparkle, title: 'Themed Decor', sub: 'Balloons, banners & more' },
  { icon: PawPrint, title: 'Play Session', sub: 'Off-leash with best friends' },
  { icon: Confetti, title: 'Party Hosting', sub: 'We handle everything' },
];

const PetCartNest = () => {
  const [bookingOpen, setBookingOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Header />
      <BookingEnquiryDialog open={bookingOpen} onOpenChange={setBookingOpen} />

      <BrandHero
        image="https://customer-assets.emergentagent.com/job_petcart-landing/artifacts/lz3r7pk3_IMG-20190303-WA0031%20%281%29.jpg"
        imageAlt="PetCart Nest resort exterior"
        className="min-h-[560px] lg:min-h-[78vh]"
      >
        <BrandHeroCopy
          logo={['/images/logo-petcart.png', '/images/logo-nest.png']}
          logoAlt="PetCart Nest"
          eyebrow="Premium pet resort"
          phrases={['More than a stay,', "It's their happy place."]}
          subtitle="Where tails wag, hearts relax and every stay feels like home."
          features={[
            { icon: House, label: 'Premium stay & daycare', sub: 'A campus that feels like home' },
            { icon: Drop, label: 'Swimming & hydro fun', sub: 'Supervised splash sessions' },
            { icon: Sparkle, label: 'Grooming spa', sub: 'Fresh, calm, cared for' },
          ]}
          actions={(
            <>
              <button
                type="button"
                onClick={() => setBookingOpen(true)}
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-full bg-primary px-6 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
              >
                <CalendarCheck size={16} weight="bold" />
                Book Now
              </button>
              <a
                href="https://wa.me/918861712345?text=Hi%20Petcart%2C%20I%27d%20like%20to%20book%20a%20stay%20at%20PetCart%20Nest."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-full border border-white/40 bg-white/10 px-6 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/20"
              >
                WhatsApp Us
              </a>
            </>
          )}
        />
      </BrandHero>

      {/* Campus — reused bento from landing */}
      <OurFacility />

      {/* Services */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="landing-section-header">
            <span className="landing-eyebrow !bg-transparent !border-primary/30 !rounded-md">
              Services
            </span>
            <h2 className="landing-section-title">Everything your pet needs</h2>
            <p className="landing-section-lede mx-auto">
              From overnight boarding to spa days — all under one roof
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s) => (
              <div
                key={s.title}
                className="rounded-2xl border border-neutral-200 bg-white p-6 transition-shadow hover:shadow-md"
              >
                <div className={`mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg ${s.color}`}>
                  <s.icon size={20} weight="duotone" />
                </div>
                <h3 className="text-base font-semibold text-neutral-900">{s.title}</h3>
                <p className="mt-2 text-sm font-normal leading-relaxed text-neutral-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Birthday Bash */}
      <section className="relative overflow-hidden bg-[#7a5a32] py-16 sm:py-20">
        <img
          src="https://images.unsplash.com/photo-1583337426008-2fef51aa841e?auto=format&fit=crop&w=2000&q=80"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#7a5a32]/95 via-[#8b6a3c]/90 to-[#6b4d2a]/95" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div className="text-white">
              <span className="inline-flex items-center gap-1 rounded-md border border-white/30 bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider backdrop-blur">
                <Star size={12} weight="fill" />
                Popular
              </span>
              <h2 className="mt-4 text-3xl font-semibold leading-tight sm:text-4xl">
                Throw your pet a birthday bash!
              </h2>
              <p className="mt-4 max-w-md text-base font-normal leading-relaxed text-white/85">
                Celebrate their milestones like family. Custom cakes, themed decor, a photo shoot, and off-leash play with their best friends — all at our resort.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href="tel:+918861712345"
                  className="inline-flex h-11 items-center justify-center gap-1.5 rounded-full bg-white px-6 text-sm font-semibold text-[#7a5a32] transition-colors hover:bg-white/90"
                >
                  <Phone size={16} weight="bold" />
                  Call to Reserve
                </a>
                <a
                  href="https://wa.me/918861712345?text=Hi%20Petcart%2C%20I%27d%20like%20to%20plan%20a%20birthday%20at%20PetCart%20Nest."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-11 items-center justify-center gap-1.5 rounded-full border border-white/40 bg-transparent px-6 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                >
                  <span
                    aria-hidden="true"
                    className="h-4 w-4 bg-current"
                    style={{
                      WebkitMaskImage: 'url(/images/whatsapp.svg)',
                      maskImage: 'url(/images/whatsapp.svg)',
                      WebkitMaskRepeat: 'no-repeat',
                      maskRepeat: 'no-repeat',
                      WebkitMaskSize: 'contain',
                      maskSize: 'contain',
                      WebkitMaskPosition: 'center',
                      maskPosition: 'center',
                    }}
                  />
                  WhatsApp Us
                </a>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-5 text-xs text-white/80">
                <span className="inline-flex items-center gap-1.5">
                  <Check size={14} weight="bold" /> By appointment
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Check size={14} weight="bold" /> At our resort
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {birthdayFeatures.map((f) => (
                <div
                  key={f.title}
                  className="rounded-xl border border-white/15 bg-white/10 p-4 text-white backdrop-blur"
                >
                  <f.icon size={20} weight="duotone" className="mb-2 text-white" />
                  <p className="text-xs font-semibold leading-tight">{f.title}</p>
                  <p className="mt-1 text-[10px] font-light leading-snug text-white/70">{f.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PetCartNest;
